'use client';

import { HStack } from '@terra/ui-styled-system';
import { Field } from '../../../../../components/field';
import { Badge } from '../../../../../components/badge';
import { Tooltip } from '../../../../../components/tooltip';
import { IconButton } from '../../../../../components/icon-button';
import { Undo02Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { SearchHarEntryResult } from '../../../finding-search';
import type { SearchAssetResult } from '../../../finding-search';
import {
  EndpointSelector,
  type EndpointSelectorPreview,
} from '../modals/EndpointSelector';
import { HarEntrySelector } from '../modals/HarEntrySelector';
import type { UpdateSignalData } from '../utils/update-signal-utils';

type UpdateSignalAssetLinkingFieldsProps = {
  isEndpointEditable: boolean;
  editedData: UpdateSignalData;
  resolvedEndpointId?: string;
  resolvedHarEntryId?: string;
  effectiveEndpointId?: string;
  stagedEndpointPreview?: EndpointSelectorPreview;
  currentEndpointPlaceholder?: string;
  currentHarEntryPlaceholder?: string;
  onEndpointChange: (endpointId: string | undefined) => void;
  onHarEntryChange: (harEntryId: string | undefined) => void;
  onSearchAssets?: (query: string) => Promise<SearchAssetResult[]>;
  onGetHarEntriesByEndpoint?: (
    endpointId: string,
  ) => Promise<SearchHarEntryResult[]>;
  isFieldModified: (field: keyof UpdateSignalData) => boolean;
  onResetField: (field: keyof Omit<UpdateSignalData, 'signalId'>) => void;
};

export function UpdateSignalAssetLinkingFields({
  isEndpointEditable,
  editedData,
  resolvedEndpointId,
  resolvedHarEntryId,
  effectiveEndpointId,
  stagedEndpointPreview,
  currentEndpointPlaceholder,
  currentHarEntryPlaceholder,
  onEndpointChange,
  onHarEntryChange,
  onSearchAssets,
  onGetHarEntriesByEndpoint,
  isFieldModified,
  onResetField,
}: UpdateSignalAssetLinkingFieldsProps) {
  const t = useTranslations('designSystem.chat.updateSignalModal');
  const fieldResetControl = (
    field: keyof Omit<UpdateSignalData, 'signalId'>,
  ) =>
    isFieldModified(field) ? (
      <HStack gap={1}>
        <Badge variant="emphasis" size="sm">
          {t('modified')}
        </Badge>
        <Tooltip title={t('resetToOriginal')}>
          <IconButton
            variant="ghost"
            size="xs"
            onClick={() => onResetField(field)}
            aria-label={t('resetField')}
          >
            <Undo02Icon size={12} />
          </IconButton>
        </Tooltip>
      </HStack>
    ) : null;

  return (
    <>
      <Field.Root width="100%" disabled={!isEndpointEditable}>
        <HStack justifyContent="space-between" alignItems="center">
          <Field.Label fontSize="xs" fontWeight="medium">
            {t('fields.endpoint')}
          </Field.Label>
          {isEndpointEditable ? fieldResetControl('endpointId') : null}
        </HStack>
        <Field.HelperText fontSize="2xs" color="fg.muted">
          {isEndpointEditable
            ? t('typeToSearchEndpoints')
            : t('endpointNotEditableHelper')}
        </Field.HelperText>
        <EndpointSelector
          value={
            isEndpointEditable ? editedData.endpointId : resolvedEndpointId
          }
          onChange={onEndpointChange}
          onSearchAssets={onSearchAssets}
          disabled={!isEndpointEditable}
          selectedPreview={stagedEndpointPreview}
          currentEndpointPlaceholder={currentEndpointPlaceholder}
        />
      </Field.Root>

      <Field.Root width="100%" disabled={!isEndpointEditable}>
        <HStack justifyContent="space-between" alignItems="center">
          <Field.Label fontSize="xs" fontWeight="medium">
            {t('fields.harEntryId')}
          </Field.Label>
          {isEndpointEditable ? fieldResetControl('harEntryId') : null}
        </HStack>
        <Field.HelperText fontSize="2xs" color="fg.muted">
          {isEndpointEditable
            ? t('fields.harEntryIdNote')
            : t('endpointNotEditableHelper')}
        </Field.HelperText>
        <HarEntrySelector
          endpointId={effectiveEndpointId}
          value={
            isEndpointEditable ? editedData.harEntryId : resolvedHarEntryId
          }
          onChange={onHarEntryChange}
          onGetHarEntriesByEndpoint={onGetHarEntriesByEndpoint}
          disabled={!isEndpointEditable}
          currentHarEntryPlaceholder={currentHarEntryPlaceholder}
        />
      </Field.Root>
    </>
  );
}
